// src/features/datev/components/SteuerIdAntraege.tsx
//
// Reiter "Steuer-ID-Anträge" im DATEV-Bereich (XTAX GmbH & Co. KG, 14.10.2026).
//
// Massenaktion je Mandant: Angeboten werden aktive und im laufenden Jahr
// ausgetretene Mitarbeiter ohne Steuer-ID mit Wohnsitz im Ausland aus dem
// DATEV-Spiegel (Personenkreis seit 03.11.2026). Mehrere Datensätze derselben
// Person (Wiedereintritte) fasst der Server zu einer Zeile zusammen.
//
// Ausweise werden gesammelt hochgeladen, auch als ZIP-Archiv, und über die
// Personalnummer, die betriebliche Personalnummer oder die Kundennummer im
// Dateinamen zugeordnet. Nicht eindeutig zuordenbare Dateien werden von Hand
// zugeordnet. Zeigen mehrere Dateien eines Uploads auf denselben Mitarbeiter,
// wird keine davon automatisch übernommen; alle landen bei "Von Hand
// zuordnen", damit bewusst die richtige gewählt wird.
//
// Nach Prüfung der Vorschau und Bestätigung des Absenders legt der Klick einen
// Auftrag an. Versendet wird gedrosselt im Hintergrund (Netlify-Funktion
// steuerid-antrag-worker), der Fortschritt ist hier sichtbar.
//
// Versandbereitschaft, fehlende Angaben und Empfänger berechnet ausschließlich
// der Server (steuerid-antrag.js), damit Anzeige und Versand nie auseinander
// laufen. Absender-Sperre wie bei der Finalisierung: Solange der Absender nicht
// über die Anmeldung bestätigt oder bewusst gewählt ist, ist die Beauftragung
// gesperrt.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, AlertCircle, Upload, Eye, ShieldAlert, Send, CheckCircle } from 'lucide-react';
import { useAuth } from '../../auth/hooks/useAuth';
import { useSenderManagement } from '../../deadlines/hooks/useSenderManagement';
import { SenderSelection } from '../../deadlines/components/SenderSelection';
import { EmailSenderIdentity } from '../../../services/email/types';
import {
  steuerIdAntragService,
  ordneDateiZu,
  pruefeAusweisDatei,
  istZipArchiv,
  entpackeAusweisArchiv,
  SteuerIdAntragFehler,
  SteuerIdKandidat,
  SteuerIdUebersicht
} from '../../../services/datev/SteuerIdAntragService';

interface SteuerIdAntraegeProps {
  customerId: string;
  company: string;
}

interface OffeneDatei {
  id: string;
  datei: File;
  ziel: string;
  hinweis: string | null;
}

const AKTUALISIERUNG_MS = 15000;
const DATEI_AUSWAHL = '.pdf,.jpg,.jpeg,.png,.zip,application/pdf,image/jpeg,image/png,application/zip';

function fmtDatum(wert: string | null | undefined): string {
  if (!wert) return '';
  const d = new Date(wert);
  if (isNaN(d.getTime())) return String(wert);
  return d.toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtZeitpunkt(wert: string | null | undefined): string {
  if (!wert) return '';
  const d = new Date(wert);
  if (isNaN(d.getTime())) return String(wert);
  return d.toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  }) + ' Uhr';
}

function fehlerText(err: unknown, fallback: string): { meldung: string; details: string[] } {
  if (err instanceof SteuerIdAntragFehler) return { meldung: err.message, details: err.details };
  if (err instanceof Error) return { meldung: err.message, details: [] };
  return { meldung: fallback, details: [] };
}

function neueOffeneDatei(datei: File, ziel: string, hinweis: string | null): OffeneDatei {
  return { id: `${datei.name}-${datei.size}-${Math.random().toString(36).slice(2, 8)}`, datei, ziel, hinweis };
}

const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 12px', color: '#6B7280', fontWeight: 500,
  fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px'
};

const tdStyle: React.CSSProperties = { padding: '9px 12px', fontSize: '13px', color: '#374151', verticalAlign: 'top' };

const kleinerButton: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '4px',
  padding: '4px 8px', backgroundColor: 'white', color: '#374151',
  border: '1px solid #D1D5DB', borderRadius: '5px', cursor: 'pointer', fontSize: '12px'
};

const boxStyle = (bg: string, border: string, color: string): React.CSSProperties => ({
  backgroundColor: bg, border: `1px solid ${border}`, borderRadius: '6px',
  padding: '12px', marginBottom: '16px', fontSize: '13px', color, lineHeight: 1.5
});

export const SteuerIdAntraege: React.FC<SteuerIdAntraegeProps> = ({ customerId, company }) => {
  const [uebersicht, setUebersicht] = useState<SteuerIdUebersicht | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [uploadLaeuft, setUploadLaeuft] = useState(false);
  const [uploadMeldungen, setUploadMeldungen] = useState<string[]>([]);
  const [offeneDateien, setOffeneDateien] = useState<OffeneDatei[]>([]);
  const [vorschauFuer, setVorschauFuer] = useState<string | null>(null);
  const [einzelZiel, setEinzelZiel] = useState<SteuerIdKandidat | null>(null);

  const [beauftragtLaeuft, setBeauftragtLaeuft] = useState(false);
  const [auftragFehler, setAuftragFehler] = useState<{ meldung: string; details: string[] } | null>(null);
  const [auftragErfolg, setAuftragErfolg] = useState<string | null>(null);

  const sammelInputRef = useRef<HTMLInputElement>(null);
  const einzelInputRef = useRef<HTMLInputElement>(null);

  const { user } = useAuth();
  const {
    senders,
    selectedSender,
    setSelectedSender,
    loading: sendersLoading,
    error: sendersError,
    photoValidationErrors,
    senderIdentityUnconfirmed
  } = useSenderManagement(true, user?.email);

  // ============================================
  // DATEN LADEN
  // ============================================

  const laden = useCallback(async (mitAuswahl: boolean) => {
    setLoadError(null);
    try {
      const daten = await steuerIdAntragService.ladeUebersicht(customerId);
      setUebersicht(daten);
      if (mitAuswahl) {
        // Vorauswahl: alle versandbereiten, die noch nicht beantragt wurden.
        setAuswahl(new Set(
          daten.kandidaten
            .filter(k => k.bereit && k.letzter_antrag?.status !== 'versendet')
            .map(k => k.datev_employee_id)
        ));
      } else {
        // Auswahl auf weiterhin versandbereite Mitarbeiter begrenzen.
        const bereit = new Set(daten.kandidaten.filter(k => k.bereit).map(k => k.datev_employee_id));
        setAuswahl(prev => new Set(Array.from(prev).filter(id => bereit.has(id))));
      }
    } catch (err) {
      setLoadError(fehlerText(err, 'Unbekannter Fehler beim Laden').meldung);
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    setIsLoading(true);
    setUebersicht(null);
    setAuswahl(new Set());
    setUploadMeldungen([]);
    setOffeneDateien([]);
    setAuftragFehler(null);
    setAuftragErfolg(null);
    laden(true);
  }, [laden]);

  // Fortschritt laufender Aufträge automatisch nachladen.
  const auftragLaeuft = !!uebersicht?.auftraege.some(a => a.status === 'laeuft');
  useEffect(() => {
    if (!auftragLaeuft) return;
    const t = setInterval(() => { laden(false); }, AKTUALISIERUNG_MS);
    return () => clearInterval(t);
  }, [auftragLaeuft, laden]);

  // ============================================
  // ABGELEITETE DATEN
  // ============================================

  const kandidaten = useMemo(() => uebersicht?.kandidaten ?? [], [uebersicht]);
  const bereitListe = useMemo(() => kandidaten.filter(k => k.bereit), [kandidaten]);
  const anzahlAusgetreten = useMemo(() => kandidaten.filter(k => !!k.ausgetreten_am).length, [kandidaten]);
  const einrichtungProbleme = uebersicht?.einrichtung_probleme ?? [];
  const ausgewaehlt = useMemo(
    () => kandidaten.filter(k => k.bereit && auswahl.has(k.datev_employee_id)),
    [kandidaten, auswahl]
  );

  // ============================================
  // AUSWEISE
  // ============================================

  const ladeHoch = async (kandidat: SteuerIdKandidat, datei: File) => {
    await steuerIdAntragService.ladeAusweisHoch(customerId, kandidat.personalnummer, datei, user?.email);
  };

  const handleSammelUpload = async (auswahlDateien: FileList | null) => {
    if (!auswahlDateien || auswahlDateien.length === 0) return;
    setUploadLaeuft(true);
    const meldungen: string[] = [];
    const offen: OffeneDatei[] = [];

    // 1. ZIP-Archive entpacken, übrige Dateien unverändert übernehmen.
    const dateien: File[] = [];
    for (const datei of Array.from(auswahlDateien)) {
      if (!istZipArchiv(datei)) {
        dateien.push(datei);
        continue;
      }
      try {
        const { dateien: entpackt, uebersprungen } = await entpackeAusweisArchiv(datei);
        dateien.push(...entpackt);
        if (uebersprungen.length > 0) {
          meldungen.push(`${datei.name}: ${uebersprungen.length} Datei${uebersprungen.length === 1 ? '' : 'en'} übersprungen, weil nur PDF, JPG oder PNG übernommen werden (${uebersprungen.join(', ')}).`);
        }
      } catch (err) {
        meldungen.push(fehlerText(err, `${datei.name}: Das Archiv konnte nicht gelesen werden.`).meldung);
      }
    }

    // 2. Prüfen und zuordnen.
    const zuordnungen: Array<{ datei: File; ziel: SteuerIdKandidat }> = [];
    for (const datei of dateien) {
      const pruefFehler = pruefeAusweisDatei(datei);
      if (pruefFehler) { meldungen.push(pruefFehler); continue; }
      const ziel = ordneDateiZu(datei.name, kandidaten);
      if (!ziel) {
        offen.push(neueOffeneDatei(datei, '', null));
        continue;
      }
      zuordnungen.push({ datei, ziel });
    }

    // 3. Mehrere Dateien für denselben Mitarbeiter: keine automatisch übernehmen.
    const anzahlJeZiel = new Map<string, number>();
    for (const z of zuordnungen) {
      anzahlJeZiel.set(z.ziel.datev_employee_id, (anzahlJeZiel.get(z.ziel.datev_employee_id) || 0) + 1);
    }
    const eindeutig = zuordnungen.filter(z => anzahlJeZiel.get(z.ziel.datev_employee_id) === 1);
    const doppelt = zuordnungen.filter(z => (anzahlJeZiel.get(z.ziel.datev_employee_id) || 0) > 1);
    for (const z of doppelt) {
      offen.push(neueOffeneDatei(
        z.datei,
        z.ziel.datev_employee_id,
        `mehrere Dateien für PNr ${z.ziel.personalnummer}, bitte die richtige zuordnen und die übrigen verwerfen`
      ));
    }

    // 4. Eindeutige Zuordnungen hochladen.
    let erfolgreich = 0;
    for (const z of eindeutig) {
      try {
        await ladeHoch(z.ziel, z.datei);
        erfolgreich += 1;
      } catch (err) {
        meldungen.push(fehlerText(err, `${z.datei.name}: Upload fehlgeschlagen.`).meldung);
      }
    }

    if (erfolgreich > 0) meldungen.unshift(`${erfolgreich} Ausweis${erfolgreich === 1 ? '' : 'e'} automatisch zugeordnet.`);
    const doppeltZiele = new Set(doppelt.map(z => z.ziel.datev_employee_id)).size;
    if (doppeltZiele > 0) {
      meldungen.push(`Für ${doppeltZiele} Mitarbeiter liegen mehrere Dateien vor. Bitte unten jeweils die richtige zuordnen.`);
    }
    const nichtZuordenbar = offen.length - doppelt.length;
    if (nichtZuordenbar > 0) {
      meldungen.push(`${nichtZuordenbar} Datei${nichtZuordenbar === 1 ? '' : 'en'} nicht eindeutig zuordenbar. Bitte unten von Hand zuordnen.`);
    }
    setUploadMeldungen(meldungen);
    setOffeneDateien(prev => [...prev, ...offen]);
    setUploadLaeuft(false);
    if (sammelInputRef.current) sammelInputRef.current.value = '';
    await laden(false);
  };

  const handleManuellZuordnen = async (eintrag: OffeneDatei) => {
    const ziel = kandidaten.find(k => k.datev_employee_id === eintrag.ziel);
    if (!ziel) return;
    setUploadLaeuft(true);
    try {
      await ladeHoch(ziel, eintrag.datei);
      setOffeneDateien(prev => prev.filter(o => o.id !== eintrag.id));
      setUploadMeldungen([`${eintrag.datei.name} wurde PNr ${ziel.personalnummer} zugeordnet.`]);
      await laden(false);
    } catch (err) {
      setUploadMeldungen([fehlerText(err, 'Upload fehlgeschlagen.').meldung]);
    } finally {
      setUploadLaeuft(false);
    }
  };

  const handleEinzelUpload = async (dateien: FileList | null) => {
    const ziel = einzelZiel;
    setEinzelZiel(null);
    if (!ziel || !dateien || dateien.length === 0) return;
    setUploadLaeuft(true);
    try {
      await ladeHoch(ziel, dateien[0]);
      setUploadMeldungen([`Ausweis für PNr ${ziel.personalnummer} gespeichert.`]);
      await laden(false);
    } catch (err) {
      setUploadMeldungen([fehlerText(err, 'Upload fehlgeschlagen.').meldung]);
    } finally {
      setUploadLaeuft(false);
      if (einzelInputRef.current) einzelInputRef.current.value = '';
    }
  };

  const starteEinzelUpload = (kandidat: SteuerIdKandidat) => {
    setEinzelZiel(kandidat);
    einzelInputRef.current?.click();
  };

  // ============================================
  // VORSCHAU
  // ============================================

  const handleVorschau = async (kandidat: SteuerIdKandidat) => {
    // Fenster sofort öffnen, damit der Browser es nicht als Pop-up blockiert.
    const fenster = window.open('', '_blank');
    setVorschauFuer(kandidat.datev_employee_id);
    try {
      const { pdf } = await steuerIdAntragService.ladeVorschau(customerId, kandidat.datev_employee_id);
      const bytes = Uint8Array.from(atob(pdf), c => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      if (fenster) {
        fenster.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch (err) {
      if (fenster) fenster.close();
      const f = fehlerText(err, 'Vorschau fehlgeschlagen.');
      setUploadMeldungen([`Vorschau PNr ${kandidat.personalnummer}: ${f.meldung}${f.details.length ? ` (${f.details.join(', ')})` : ''}`]);
    } finally {
      setVorschauFuer(null);
    }
  };

  // ============================================
  // AUSWAHL UND BEAUFTRAGUNG
  // ============================================

  const toggleAuswahl = (id: string) => {
    setAuswahl(prev => {
      const neu = new Set(prev);
      if (neu.has(id)) neu.delete(id); else neu.add(id);
      return neu;
    });
  };

  const handleSenderChange = (sender: EmailSenderIdentity) => {
    setSelectedSender(sender);
  };

  // Erzwungener Relogin wie in der Finalisierung (ReleaseModal).
  const handleForceRelogin = () => {
    localStorage.removeItem('authToken');
    window.location.reload();
  };

  const absenderFotoFehler = !!(selectedSender && photoValidationErrors[selectedSender.id]);
  const beauftragenGesperrt =
    beauftragtLaeuft ||
    ausgewaehlt.length === 0 ||
    einrichtungProbleme.length > 0 ||
    sendersLoading ||
    !selectedSender ||
    absenderFotoFehler ||
    senderIdentityUnconfirmed;

  const handleBeauftragen = async () => {
    if (beauftragenGesperrt || !selectedSender) return;
    const anzahl = ausgewaehlt.length;
    const ok = window.confirm(
      `${anzahl} Antrag${anzahl === 1 ? '' : 'e'} für ${company} an das ${uebersicht?.finanzamt ?? 'Finanzamt'} senden?\n\n` +
      `Absender: ${selectedSender.fullName}\n` +
      `Der Versand läuft gedrosselt im Hintergrund.`
    );
    if (!ok) return;

    setBeauftragtLaeuft(true);
    setAuftragFehler(null);
    setAuftragErfolg(null);
    try {
      const ergebnis = await steuerIdAntragService.beauftrage(
        customerId,
        ausgewaehlt.map(k => k.datev_employee_id),
        selectedSender.id
      );
      setAuftragErfolg(
        `Auftrag angelegt: ${ergebnis.anzahl} Antrag${ergebnis.anzahl === 1 ? '' : 'e'} im Namen von ${ergebnis.absender}. ` +
        `Der Versand startet innerhalb der nächsten Minute.`
      );
      await laden(true);
    } catch (err) {
      setAuftragFehler(fehlerText(err, 'Auftrag konnte nicht angelegt werden.'));
    } finally {
      setBeauftragtLaeuft(false);
    }
  };

  // ============================================
  // RENDER
  // ============================================

  if (isLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>
        <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: '8px' }} />
        Steuer-ID-Anträge werden geladen...
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (loadError || !uebersicht) {
    return (
      <div style={{ ...boxStyle('#FEE2E2', '#FECACA', '#991B1B'), display: 'flex', gap: '8px' }}>
        <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
        <div>
          <strong>Fehler beim Laden:</strong> {loadError}
          <div style={{ marginTop: '8px' }}>
            <button onClick={() => { setIsLoading(true); laden(true); }} style={{ ...kleinerButton, backgroundColor: '#991B1B', color: 'white', border: 'none' }}>
              Erneut versuchen
            </button>
          </div>
        </div>
      </div>
    );
  }

  const statusZelle = (k: SteuerIdKandidat) => {
    const a = k.letzter_antrag;
    if (!a) return <span style={{ color: '#9CA3AF' }}>noch nicht beantragt</span>;
    if (a.status === 'wartend' || a.status === 'in_arbeit') {
      return <span style={{ color: '#1E40AF', fontWeight: 600 }}>im Versand</span>;
    }
    if (a.status === 'versendet') {
      return <span style={{ color: '#166534', fontWeight: 600 }}>beantragt am {fmtDatum(a.versendet_am)}{a.empfaenger ? ` (${a.empfaenger})` : ''}</span>;
    }
    return (
      <span style={{ color: '#991B1B' }}>
        <strong>{a.status === 'fehlgeschlagen' ? 'fehlgeschlagen' : 'abgebrochen'}</strong>
        {a.last_error ? `: ${a.last_error}` : ''}
      </span>
    );
  };

  return (
    <div>
      {/* Kopf */}
      <div style={boxStyle('#EFF6FF', '#DBEAFE', '#1E40AF')}>
        <strong>Steuer-ID-Anträge (Formular 010250)</strong> für aktive und im laufenden Jahr ausgetretene Mitarbeiter
        ohne Steuer-ID mit Wohnsitz im Ausland. Empfänger: <strong>{uebersicht.finanzamt ?? 'kein Finanzamt hinterlegt'}</strong>.
        Grundlage ist der DATEV-Spiegel, Stand <strong>{fmtZeitpunkt(uebersicht.spiegel_stand) || 'unbekannt'}</strong>.
        Fehlende Mitarbeiter bitte zuerst im Reiter „DATEV Lohnaustauschdatenservice“ nachladen.
      </div>

      {einrichtungProbleme.length > 0 && (
        <div style={boxStyle('#FEE2E2', '#FECACA', '#991B1B')}>
          <strong>Der Kunde ist für Steuer-ID-Anträge noch nicht vollständig eingerichtet.</strong> Bitte in den
          Kundenstammdaten (Reiter „Steuer &amp; Sozialversicherung“ bzw. „Rechnungsdaten“ für die Anschrift) ergänzen:
          <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
            {einrichtungProbleme.map(p => <li key={p}>{p}</li>)}
          </ul>
        </div>
      )}

      {/* Ausweise */}
      <div style={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px 20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>Ausweise hochladen</div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
              Eine Datei je Mitarbeiter (PDF, JPG oder PNG, höchstens 20 MB), einzeln oder als ZIP-Archiv. Im Dateinamen
              muss die Personalnummer, die betriebliche Personalnummer oder die Kundennummer (Teil der betrieblichen
              Personalnummer vor dem Punkt) stehen. Ein neuer Ausweis ersetzt den bisherigen.
            </div>
          </div>
          <button
            onClick={() => sammelInputRef.current?.click()}
            disabled={uploadLaeuft || kandidaten.length === 0}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
              backgroundColor: uploadLaeuft || kandidaten.length === 0 ? '#9CA3AF' : '#2563EB', color: 'white',
              border: 'none', borderRadius: '6px', cursor: uploadLaeuft ? 'not-allowed' : 'pointer', fontSize: '13px'
            }}
          >
            {uploadLaeuft ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={14} />}
            {uploadLaeuft ? 'Wird hochgeladen...' : 'Dateien auswählen'}
          </button>
          <input
            ref={sammelInputRef}
            type="file"
            multiple
            accept={DATEI_AUSWAHL}
            style={{ display: 'none' }}
            onChange={e => handleSammelUpload(e.target.files)}
          />
          <input
            ref={einzelInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            style={{ display: 'none' }}
            onChange={e => handleEinzelUpload(e.target.files)}
          />
        </div>

        {uploadMeldungen.length > 0 && (
          <div style={{ marginTop: '12px', fontSize: '12px', color: '#374151' }}>
            {uploadMeldungen.map((m, i) => <div key={i}>{m}</div>)}
          </div>
        )}

        {offeneDateien.length > 0 && (
          <div style={{ marginTop: '12px', borderTop: '1px solid #F3F4F6', paddingTop: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#92400E', marginBottom: '8px' }}>
              Von Hand zuordnen ({offeneDateien.length})
            </div>
            {offeneDateien.map(o => (
              <div key={o.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '12px', color: '#374151', minWidth: '220px' }}>
                  {o.datei.name}
                  {o.hinweis && <span style={{ display: 'block', color: '#B45309' }}>{o.hinweis}</span>}
                </span>
                <select
                  value={o.ziel}
                  onChange={e => setOffeneDateien(prev => prev.map(x => x.id === o.id ? { ...x, ziel: e.target.value } : x))}
                  style={{ padding: '5px 8px', border: '1px solid #D1D5DB', borderRadius: '5px', fontSize: '12px', minWidth: '260px' }}
                >
                  <option value="">Mitarbeiter wählen...</option>
                  {kandidaten.map(k => (
                    <option key={k.datev_employee_id} value={k.datev_employee_id}>
                      PNr {k.personalnummer}{k.betriebliche_nr ? ` / ${k.betriebliche_nr}` : ''}: {k.nachname}, {k.vorname}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => handleManuellZuordnen(o)}
                  disabled={!o.ziel || uploadLaeuft}
                  style={{ ...kleinerButton, opacity: !o.ziel || uploadLaeuft ? 0.5 : 1 }}
                >
                  Zuordnen
                </button>
                <button
                  onClick={() => setOffeneDateien(prev => prev.filter(x => x.id !== o.id))}
                  disabled={uploadLaeuft}
                  style={kleinerButton}
                >
                  Verwerfen
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Kandidaten */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ fontSize: '13px', color: '#374151' }}>
          <strong>{kandidaten.length}</strong> Mitarbeiter ohne Steuer-ID mit Wohnsitz im Ausland
          {anzahlAusgetreten > 0 && <> (davon <strong>{anzahlAusgetreten}</strong> im laufenden Jahr ausgetreten)</>} ·{' '}
          <strong>{bereitListe.length}</strong> versandbereit · <strong>{ausgewaehlt.length}</strong> ausgewählt
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={kleinerButton} onClick={() => setAuswahl(new Set(bereitListe.map(k => k.datev_employee_id)))}>
            Alle versandbereiten auswählen
          </button>
          <button style={kleinerButton} onClick={() => setAuswahl(new Set())}>Auswahl leeren</button>
          <button style={kleinerButton} onClick={() => laden(false)}><RefreshCw size={12} /> Aktualisieren</button>
        </div>
      </div>

      {kandidaten.length === 0 ? (
        <div style={{ padding: '32px', textAlign: 'center', color: '#6B7280', fontSize: '14px', backgroundColor: '#F9FAFB', borderRadius: '8px', marginBottom: '16px' }}>
          Im DATEV-Spiegel gibt es für diesen Mandanten keine aktiven oder im laufenden Jahr ausgetretenen Mitarbeiter
          ohne Steuer-ID mit Wohnsitz im Ausland.
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', overflowX: 'auto', marginBottom: '16px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                <th style={{ ...thStyle, width: '32px' }}></th>
                <th style={thStyle}>PNr</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Land</th>
                <th style={thStyle}>Ausweis</th>
                <th style={thStyle}>Prüfung</th>
                <th style={thStyle}>Antrag</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {kandidaten.map(k => (
                <tr key={k.datev_employee_id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td style={tdStyle}>
                    <input
                      type="checkbox"
                      checked={auswahl.has(k.datev_employee_id)}
                      disabled={!k.bereit}
                      onChange={() => toggleAuswahl(k.datev_employee_id)}
                    />
                  </td>
                  <td style={{ ...tdStyle, fontFamily: 'Menlo, Monaco, monospace' }}>
                    {k.personalnummer}
                    {k.betriebliche_nr && <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{k.betriebliche_nr}</div>}
                    {k.weitere_personalnummern.length > 0 && (
                      <div style={{ fontSize: '11px', color: '#9CA3AF' }}>auch PNr {k.weitere_personalnummern.join(', ')}</div>
                    )}
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 500, color: '#111827' }}>
                    {k.nachname}, {k.vorname}
                    {k.ausgetreten_am && (
                      <div style={{ fontSize: '11px', color: '#B45309', fontWeight: 400 }}>ausgetreten am {fmtDatum(k.ausgetreten_am)}</div>
                    )}
                    {k.empfaenger && <div style={{ fontSize: '11px', color: '#6B7280', fontWeight: 400 }}>an {k.empfaenger}</div>}
                  </td>
                  <td style={tdStyle}>{k.land ?? 'fehlt'}</td>
                  <td style={tdStyle}>
                    {k.ausweis ? (
                      <div>
                        <div style={{ color: '#166534' }}>{k.ausweis.dateiname}</div>
                        <button style={{ ...kleinerButton, marginTop: '4px' }} disabled={uploadLaeuft} onClick={() => starteEinzelUpload(k)}>
                          Ersetzen
                        </button>
                      </div>
                    ) : (
                      <button style={kleinerButton} disabled={uploadLaeuft} onClick={() => starteEinzelUpload(k)}>
                        <Upload size={12} /> Hochladen
                      </button>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {k.bereit ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#166534', fontWeight: 600 }}>
                        <CheckCircle size={13} /> versandbereit
                      </span>
                    ) : (
                      <span style={{ color: '#92400E' }}>{k.fehlende.join(', ')}</span>
                    )}
                  </td>
                  <td style={tdStyle}>{statusZelle(k)}</td>
                  <td style={tdStyle}>
                    <button
                      style={kleinerButton}
                      disabled={vorschauFuer === k.datev_employee_id}
                      onClick={() => handleVorschau(k)}
                      title="Antrag als PDF ansehen"
                    >
                      <Eye size={12} /> {vorschauFuer === k.datev_employee_id ? 'Lädt...' : 'Vorschau'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Absender und Beauftragung */}
      {kandidaten.length > 0 && (
        <div style={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px 20px', marginBottom: '16px' }}>
          {senderIdentityUnconfirmed && (
            <div style={{ ...boxStyle('#FEF3C7', '#F59E0B', '#92400E'), display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <ShieldAlert size={20} color="#F59E0B" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>Absender bitte bestätigen</div>
                <div style={{ marginBottom: '10px' }}>
                  Der Absender konnte nicht automatisch über Ihre Anmeldung bestätigt werden, möglicherweise ist Ihre
                  Sitzung abgelaufen. Voreingestellt ist <strong>{selectedSender?.fullName || 'der primäre Mitarbeiter'}</strong>.
                  Die Beauftragung ist gesperrt, bis Sie den Absender unten bewusst auswählen oder sich neu anmelden.
                </div>
                <button
                  type="button"
                  onClick={handleForceRelogin}
                  style={{ padding: '6px 12px', backgroundColor: '#F59E0B', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Neu anmelden
                </button>
              </div>
            </div>
          )}

          <div style={{ maxWidth: '480px' }}>
            <SenderSelection
              senders={senders}
              selectedSender={selectedSender}
              onSenderChange={handleSenderChange}
              disabled={beauftragtLaeuft}
              loading={sendersLoading}
              error={sendersError}
              photoValidationErrors={photoValidationErrors}
            />
          </div>

          {auftragFehler && (
            <div style={boxStyle('#FEE2E2', '#FECACA', '#991B1B')}>
              <strong>{auftragFehler.meldung}</strong>
              {auftragFehler.details.length > 0 && (
                <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                  {auftragFehler.details.map(d => <li key={d}>{d}</li>)}
                </ul>
              )}
            </div>
          )}
          {auftragErfolg && <div style={boxStyle('#F0FDF4', '#BBF7D0', '#166534')}>{auftragErfolg}</div>}

          <button
            onClick={handleBeauftragen}
            disabled={beauftragenGesperrt}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px',
              backgroundColor: beauftragenGesperrt ? '#9CA3AF' : '#10B981', color: 'white', border: 'none',
              borderRadius: '6px', cursor: beauftragenGesperrt ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 500
            }}
          >
            {beauftragtLaeuft
              ? <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Auftrag wird angelegt...</>
              : <><Send size={14} /> {ausgewaehlt.length} Antrag{ausgewaehlt.length === 1 ? '' : 'e'} versenden</>}
          </button>
        </div>
      )}

      {/* Aufträge */}
      {uebersicht.auftraege.length > 0 && (
        <div style={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px 20px', marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '10px' }}>Letzte Aufträge</div>
          {uebersicht.auftraege.map(a => (
            <div key={a.id} style={{ fontSize: '13px', color: '#374151', padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
              <strong>{fmtZeitpunkt(a.created_at)}</strong> · {a.ausgeloest_von_email} · {a.anzahl_antraege} Anträge ·{' '}
              <span style={{ color: a.status === 'laeuft' ? '#1E40AF' : '#166534', fontWeight: 600 }}>
                {a.status === 'laeuft' ? 'läuft' : a.status === 'abgeschlossen' ? 'abgeschlossen' : 'abgebrochen'}
              </span>
              {' '}({a.zaehler.versendet} versendet
              {a.zaehler.wartend + a.zaehler.in_arbeit > 0 ? `, ${a.zaehler.wartend + a.zaehler.in_arbeit} offen` : ''}
              {a.zaehler.fehlgeschlagen > 0 ? `, ${a.zaehler.fehlgeschlagen} fehlgeschlagen` : ''}
              {a.zaehler.abgebrochen > 0 ? `, ${a.zaehler.abgebrochen} abgebrochen` : ''})
            </div>
          ))}
        </div>
      )}

      {/* Erledigt */}
      {uebersicht.erledigt.length > 0 && (
        <div style={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px 20px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '10px' }}>
            Steuer-ID inzwischen vorhanden ({uebersicht.erledigt.length})
          </div>
          {uebersicht.erledigt.map(e => (
            <div key={e.personalnummer} style={{ fontSize: '13px', color: '#374151', padding: '4px 0' }}>
              PNr {e.personalnummer}: {e.name} · beantragt am {fmtDatum(e.versendet_am)}
              {e.ausweis_vorhanden ? ' · Ausweis noch gespeichert' : ''}
            </div>
          ))}
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
